import React, {useEffect, useState, useRef, useCallback, useLayoutEffect} from "react";
import KnaekPrimaryButton from "../AppComponents/KnaekPrimaryButton";
import { stringsTranslate } from "../../locales/CustomTranslater";
import { appWords } from "../../config/Constants";
import NewLocalStoresDiscountCard from "../AppComponents/NewLocalStoresDiscountCard";
import {getDiscountsCall} from "../../api/ApiCalls";
import {useDispatch, useSelector} from "react-redux";
import {updateStoreDiscountsListStateRS} from "../../redux/reduxActions/reduxStoreActions";

const initStrings = {
    section1heading: "Lokale kortingen",
    section1CounterText: "resultaten",
    sectionSearchHeading: "resultaten",
    sectionFilterHeading: "Studentenstad",
    searchPlaceholder: "Zoek een lokale korting",
    load_more_button: stringsTranslate(appWords.load_more_button),
};

export default function ListItemsStoreDiscounts({ filters, onTotalDiscountsUpdate }) {
    const dispatch = useDispatch();
    const savedState = useSelector(state => state.storeDiscountsListStateRS);

    const [storeDiscounts, setStoreDiscounts] = useState(savedState.items);
    const [totalDiscountsCount, setTotalDiscountsCount] = useState(savedState.totalCount);
    const [isLoading, setIsLoading] = useState(false);
    const [allItemsLoaded, setAllItemsLoaded] = useState(savedState.allItemsLoaded);
    const [currentPage, setCurrentPage] = useState(savedState.currentPage);
    const [initialized, setInitialized] = useState(false);
    const loadMoreButtonRef = useRef(null);
    const observer = useRef();

    const getDiscounts = useCallback(
        (currentFilters , shouldResetDiscounts) => {
            if (isLoading) return; // Keep loading condition
            setIsLoading(true);

            const params = new URLSearchParams({
                page: currentFilters.page || 1            });
            if (currentFilters.category) params.append("categoryId", currentFilters.category);
            if (currentFilters.city) params.append("cityId", currentFilters.city.id);
            if (currentFilters.searchText) params.append("query", currentFilters.searchText);

            getDiscountsCall(`?${params.toString()}`)
                .then((res) => {
                    setStoreDiscounts((prev) =>
                        shouldResetDiscounts ? res.items : [...prev, ...res.items]
                    );
                    if (onTotalDiscountsUpdate) {
                        onTotalDiscountsUpdate(res.count);
                    }
                    setTotalDiscountsCount(res.count);
                    setAllItemsLoaded(res.items.length === 0  || ((currentFilters.page === 1? 0 : storeDiscounts.length) + res.items.length >= res.count));
                })
                .catch((err) => {
                    console.error('API Error:', err);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        },
        [isLoading, onTotalDiscountsUpdate, storeDiscounts.length]
    );
    useEffect(() => {
        if (isLoading || allItemsLoaded) return;

        const loadMoreOnScroll = (entries) => {
            const target = entries[0];
            if (target.isIntersecting) {
                getDiscounts({ ...filters, page: currentPage +1  }, false);
                setCurrentPage((prevPage) => prevPage + 1);
            }
        };


        observer.current = new IntersectionObserver(loadMoreOnScroll, { threshold: 1.0 });
        if (loadMoreButtonRef.current) {
            observer.current.observe(loadMoreButtonRef.current);
        }

        return () => {
            if (observer.current) observer.current.disconnect();
        };
    }, [isLoading, allItemsLoaded]);

    // Restore scroll position after items render
    useLayoutEffect(() => {
        if (storeDiscounts.length > 0 && !initialized) {
            const savedScroll = sessionStorage.getItem('storeDiscountsListScrollPosition');
            if (savedScroll) {
                window.scrollTo(0, parseInt(savedScroll, 10));
                sessionStorage.removeItem('storeDiscountsListScrollPosition');
            }
            setInitialized(true);
        }
    }, [storeDiscounts, initialized]);

    useEffect(() => {
        // Check if filters changed from saved state
        const filtersChanged = JSON.stringify(filters) !== JSON.stringify(savedState.filters);

        if (filtersChanged) {
            // Reset and fetch fresh data
            setCurrentPage(1);
            setAllItemsLoaded(false);
            setStoreDiscounts([]);
            setInitialized(false);
            getDiscounts({ ...filters, page: 1 }, true);
        } else if (storeDiscounts.length === 0) {
            // Initial load or coming back with no saved state
            getDiscounts({ ...filters, page: 1 }, true);
        }
    }, [filters, getDiscounts, savedState.filters, storeDiscounts.length]);

    // Save state to Redux whenever it changes
    useEffect(() => {
        if (storeDiscounts.length > 0) {
            dispatch(updateStoreDiscountsListStateRS({
                items: storeDiscounts,
                currentPage,
                totalCount: totalDiscountsCount,
                filters,
                allItemsLoaded
            }));
        }
    }, [storeDiscounts, currentPage, totalDiscountsCount, filters, allItemsLoaded]);
    const handleLoadMore = useCallback(() => {
        if (isLoading || allItemsLoaded) return;

        const nextPage = currentPage + 1;
        setCurrentPage(nextPage);
        getDiscounts({ ...filters, page: nextPage }, false);
    }, [isLoading, allItemsLoaded, currentPage, filters]);

    return (
        <div className="col-lg-10 col-12 text-left border-top border-bottom">
            <section className="row justify-content-center pt-5">
                {storeDiscounts.map((item, i) => (
                    <div className="store-discount-card" key={i}>
                        <NewLocalStoresDiscountCard item={item} />
                    </div>
                ))}
            </section>
            {!allItemsLoaded && (
                <div className="col-12 d-flex justify-content-center align-content-center align-items-center">
                    <KnaekPrimaryButton
                        className="col-3 mt-2 mb-4"
                        ref={loadMoreButtonRef}
                        onClick={handleLoadMore}
                        title={isLoading ? "Loading..." : initStrings.load_more_button}
                    />
                </div>
            )}
        </div>
    );
}
